import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useEditorProject } from '../hooks/useEditorProject';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Download,
  Loader2,
  MousePointer2,
  Plus,
  Copy,
  Trash2,
  Move,
  Package,
  Palette,
  Cog,
  Info,
  Focus,
  Box,
  Maximize2,
  RotateCw,
  Cloud,
  Check,
  Eye,
  EyeOff,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Camera,
  Undo2,
  Redo2,
} from 'lucide-react';
import { toast } from 'sonner';
import ThreeViewer from '../components/3d/ThreeViewer';
import BehaviorsEditor from '../components/behavior/BehaviorsEditor';
import ModelPainter from '../components/3d/ModelPainter';
import {
  addCube,
  duplicateCube,
  deleteCube,
  updateCube,
  addBone,
} from '../utils/blockbenchParser';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MC_SCALE = 1 / 16;

const TABS = [
  { id: 'model', label: 'model', icon: Package },
  { id: 'texture', label: 'texture', icon: Palette },
  { id: 'behavior', label: 'behavior', icon: Cog },
  { id: 'info', label: 'info', icon: Info },
];

const TOOLS = [
  { id: 'select', icon: MousePointer2, label: 'select' },
  { id: 'translate', icon: Move, label: 'move', gizmo: true },
  { id: 'rotate', icon: RotateCw, label: 'rotate', gizmo: true },
  { id: 'scale', icon: Maximize2, label: 'scale', gizmo: true },
];

const ACTIONS = [
  { id: 'add_cube', icon: Plus, label: 'add_cube' },
  { id: 'duplicate', icon: Copy, label: 'duplicate' },
  { id: 'delete', icon: Trash2, label: 'delete' },
];

const EditorPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    project,
    model,
    behaviors,
    loading,
    saving,
    dirty,
    updateModel,
    updateBehaviors,
    updateProjectInfo,
    saveNow,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useEditorProject(projectId);
  const behaviorConfig = project?.behavior_config || {};

  const [activeTab, setActiveTab] = useState('model');
  const [activeTool, setActiveTool] = useState('select');
  const [selectedIds, setSelectedIds] = useState([]);
  const [collapsedBones, setCollapsedBones] = useState({});
  const [exporting, setExporting] = useState(false);
  const [capturingCover, setCapturingCover] = useState(false);
  const cameraRef = useRef({ current: null });
  const prevTabRef = useRef('model');

  // Convierte un dataURL de imagen a un cuadrado 256x256 centrado (para pack_icon)
  const cropToSquareDataUrl = useCallback(async (dataUrl, size = 256) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const s = Math.min(img.width, img.height);
        const sx = (img.width - s) / 2;
        const sy = (img.height - s) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }, []);

  // Captura el estado 3D actual del modelo como imagen de portada (cover_image)
  const captureCoverImage = useCallback(async () => {
    const raw = cameraRef.current?.captureCover?.();
    if (!raw) return null;
    const cropped = await cropToSquareDataUrl(raw, 256);
    await updateProjectInfo({ cover_image: cropped });
    return cropped;
  }, [cropToSquareDataUrl, updateProjectInfo]);

  const handleCaptureCover = useCallback(async () => {
    setCapturingCover(true);
    try {
      const result = await captureCoverImage();
      if (result) {
        toast.success('Imagen de portada actualizada');
      } else {
        toast.error('No se pudo capturar la portada');
      }
    } finally {
      setCapturingCover(false);
    }
  }, [captureCoverImage]);

  // Auto-captura silenciosa cuando el usuario abandona la pestaña Modelo
  // (sólo si el visor 3D estaba montado)
  React.useEffect(() => {
    const prev = prevTabRef.current;
    if (prev === 'model' && activeTab !== 'model' && cameraRef.current?.captureCover) {
      captureCoverImage().catch(() => {});
    }
    prevTabRef.current = activeTab;
  }, [activeTab, captureCoverImage]);

  // Atajos de teclado: Ctrl+Z (Undo), Ctrl+Y / Ctrl+Shift+Z (Redo)
  React.useEffect(() => {
    const handleKey = (e) => {
      const target = e.target;
      // Ignorar cuando el foco está en un input, textarea o contenteditable
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const isCtrl = e.ctrlKey || e.metaKey;
      if (!isCtrl) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (undo()) toast.info('Deshecho');
      } else if ((key === 'y') || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        if (redo()) toast.info('Rehecho');
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [undo, redo]);

  // El "primary" cube = último seleccionado (para el panel de propiedades)
  const primaryId = selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null;
  const selectedCube = model?.cubes.find((c) => c.id === primaryId);

  // Selección con soporte multi-select (Ctrl/Shift + click)
  const handleSelectCube = useCallback((cubeId, modifiers = {}) => {
    if (cubeId === null || cubeId === undefined) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds((prev) => {
      if (modifiers.ctrlKey || modifiers.shiftKey) {
        // Toggle: si ya está, quitar; si no, añadir
        if (prev.includes(cubeId)) return prev.filter((id) => id !== cubeId);
        return [...prev, cubeId];
      }
      return [cubeId];
    });
  }, []);

  // ============================================
  // Handlers de cubos
  // ============================================
  const handleAddCube = useCallback(() => {
    if (!model) return;
    // Posición: si hay un cubo seleccionado, colocar al lado; si no, en el origen
    let position = [0, 0, 0];
    let parent = 'root';
    if (primaryId) {
      const ref = model.cubes.find((c) => c.id === primaryId);
      if (ref) {
        // Colocar al lado del cubo seleccionado (justo en su borde +X)
        position = [ref.from[0] + ref.size[0], ref.from[1], ref.from[2]];
        parent = ref.parent || 'root';
      }
    }
    const newModel = addCube(model, {
      name: `cube_${model.cubes.length + 1}`,
      position,
      size: [1, 1, 1],
      parent,
    });
    const newCube = newModel.cubes[newModel.cubes.length - 1];
    updateModel(newModel);
    setSelectedIds([newCube.id]);
    toast.success('Cubo añadido (1×1×1)');
  }, [model, primaryId, updateModel]);

  const handleDuplicateCubes = useCallback(() => {
    if (!model || selectedIds.length === 0) return;
    let workingModel = model;
    const newIds = [];
    selectedIds.forEach((id) => {
      // Duplicado en la MISMA posición y con las MISMAS UVs
      workingModel = duplicateCube(workingModel, id, [0, 0, 0]);
      const newCube = workingModel.cubes[workingModel.cubes.length - 1];
      newIds.push(newCube.id);
    });
    updateModel(workingModel);
    setSelectedIds(newIds);
    toast.success(`${newIds.length} cubo(s) duplicado(s)`);
  }, [model, selectedIds, updateModel]);

  const handleDeleteCubes = useCallback(() => {
    if (!model || selectedIds.length === 0) return;
    let workingModel = model;
    selectedIds.forEach((id) => {
      workingModel = deleteCube(workingModel, id);
    });
    updateModel(workingModel);
    setSelectedIds([]);
    toast.success(`${selectedIds.length} cubo(s) eliminado(s)`);
  }, [model, selectedIds, updateModel]);

  const handleAddBone = useCallback(() => {
    if (!model) return;
    const name = window.prompt('Nombre del nuevo bone (ej: Head, Body, Right Arm):', '');
    if (!name || !name.trim()) return;
    const newModel = addBone(model, name.trim());
    if (newModel === model) {
      toast.error('Ya existe un bone con ese nombre');
      return;
    }
    updateModel(newModel);
    toast.success(`Bone "${name.trim()}" añadido`);
  }, [model, updateModel]);

  const handleToolAction = useCallback((toolId) => {
    if (toolId === 'add_cube') {
      handleAddCube();
    } else if (toolId === 'duplicate') {
      handleDuplicateCubes();
    } else if (toolId === 'delete') {
      handleDeleteCubes();
    } else {
      setActiveTool(toolId);
    }
  }, [handleAddCube, handleDuplicateCubes, handleDeleteCubes]);

  // Aplicar transformación (commit) a TODOS los cubos seleccionados
  // startSnap contiene los datos iniciales de cada cube (from, to, size, rotationDegrees)
  const handleCubesTransform = useCallback((ids, delta, startSnap, mode) => {
    if (!model || !ids || ids.length === 0 || !startSnap) return;
    // Delta three.js → Blockbench units (redondeado a enteros)
    const dx = Math.round(delta.position[0] / MC_SCALE);
    const dy = Math.round(delta.position[1] / MC_SCALE);
    const dz = Math.round(delta.position[2] / MC_SCALE);

    let workingModel = model;
    ids.forEach((id) => {
      const snap = startSnap.meshes[id];
      if (!snap) return;
      const updates = {};

      if (mode === 'translate') {
        if (dx === 0 && dy === 0 && dz === 0) return;
        updates.position = [
          snap.from[0] + dx,
          snap.from[1] + dy,
          snap.from[2] + dz,
        ];
      } else if (mode === 'rotate') {
        const rDeg = [
          Math.round((delta.rotation[0] * 180) / Math.PI),
          Math.round((delta.rotation[1] * 180) / Math.PI),
          Math.round((delta.rotation[2] * 180) / Math.PI),
        ];
        if (rDeg[0] === 0 && rDeg[1] === 0 && rDeg[2] === 0) return;
        const newRotDeg = [
          snap.rotationDegrees[0] + rDeg[0],
          snap.rotationDegrees[1] + rDeg[1],
          snap.rotationDegrees[2] + rDeg[2],
        ];
        updates.rotationDegrees = newRotDeg;
        updates.rotation = newRotDeg.map((r) => (r * Math.PI) / 180);
      } else if (mode === 'scale') {
        const newSize = [
          Math.max(1, Math.round(snap.size[0] * delta.scale[0])),
          Math.max(1, Math.round(snap.size[1] * delta.scale[1])),
          Math.max(1, Math.round(snap.size[2] * delta.scale[2])),
        ];
        if (newSize[0] === snap.size[0] && newSize[1] === snap.size[1] && newSize[2] === snap.size[2]) return;
        updates.size = newSize;
      }

      if (Object.keys(updates).length > 0) {
        workingModel = updateCube(workingModel, id, updates);
      }
    });
    updateModel(workingModel);
  }, [model, updateModel]);

  // Editar propiedad numérica del cubo primario
  const handleUpdateProperty = (property, index, value) => {
    if (!model || !primaryId) return;
    const cube = model.cubes.find((c) => c.id === primaryId);
    if (!cube) return;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    const newArray = [...cube[property]];
    newArray[index] = numValue;
    updateModel(updateCube(model, primaryId, { [property]: newArray }));
  };

  const handleUpdateRotation = (index, value) => {
    if (!model || !primaryId) return;
    const cube = model.cubes.find((c) => c.id === primaryId);
    if (!cube) return;
    const val = parseFloat(value) || 0;
    const newRotDeg = [...cube.rotationDegrees];
    newRotDeg[index] = val;
    updateModel(updateCube(model, primaryId, {
      rotationDegrees: newRotDeg,
      rotation: newRotDeg.map((r) => (r * Math.PI) / 180),
    }));
  };

  const handleUpdateUV = (index, value) => {
    if (!model || !primaryId) return;
    const cube = model.cubes.find((c) => c.id === primaryId);
    if (!cube) return;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    const newUV = [...cube.uv_offset];
    newUV[index] = numValue;
    updateModel(updateCube(model, primaryId, { uv_offset: newUV }));
  };

  const handleToggleVisibility = (cubeId) => {
    if (!model) return;
    const cube = model.cubes.find((c) => c.id === cubeId);
    if (!cube) return;
    updateModel(updateCube(model, cubeId, { visible: cube.visible === false ? true : false }));
  };

  const toggleBoneCollapsed = (boneName) => {
    setCollapsedBones((prev) => ({ ...prev, [boneName]: !prev[boneName] }));
  };

  const handleExport = async () => {
    if (!project) return;
    // 1) Guardar cambios pendientes (geometría/textura/behaviors)
    await saveNow();
    // 2) Capturar imagen de portada actual desde el visor 3D (si estamos en la pestaña de modelo)
    //    Si el usuario está en otras pestañas puede que no haya visor cargado — se ignora silenciosamente.
    if (activeTab === 'model') {
      try {
        await captureCoverImage();
      } catch (e) {
        // Silencioso: continuar la exportación aunque falle la portada
      }
    }
    setExporting(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export`, {
        responseType: 'blob',
      });
      // Extraer nombre real del header Content-Disposition (incluye la versión bump)
      const disp = response.headers?.['content-disposition'] || '';
      const match = disp.match(/filename=("?)([^";]+)\1/i);
      const filename = match ? match[2] : `${project.name.replace(/ /g, '_')}.mcaddon`;
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Addon exportado', { description: filename });
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Construir árbol de bones para el outliner
  // Si el bone es genérico ("bone") y contiene un único cubo con nombre semántico,
  // usar el nombre del cubo como label del bone (para modelos Blockbench que
  // ponen los nombres en los elementos en lugar de los grupos).
  const boneTree = useMemo(() => {
    if (!model?.bones) return [];
    const cubesMap = new Map(model.cubes.map((c) => [c.id, c]));
    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
    return model.bones.map((bone) => {
      const cubeObjs = bone.cubes.map((id) => cubesMap.get(id)).filter(Boolean);
      let displayName = bone.name;
      if ((bone.name === 'bone' || !bone.name) && cubeObjs.length > 0) {
        // Heurística: usar el nombre del primer cubo como nombre visual del bone
        displayName = capitalize(cubeObjs[0].name || 'bone');
      } else {
        displayName = capitalize(bone.name);
      }
      return { ...bone, cubeObjs, displayName };
    });
  }, [model?.bones, model?.cubes]);

  if (loading || !model) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Proyecto no encontrado</p>
          <Button onClick={() => navigate('/')}>Volver</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="editor-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="text-gray-600 hover:text-gray-900 h-8"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="border-l border-gray-200 pl-3">
            <h1 className="text-sm font-semibold text-gray-900 leading-tight">{project.name}</h1>
            <p className="text-xs text-gray-500 leading-tight">{project.author}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 mr-1 border border-gray-200 rounded-md overflow-hidden">
            <button
              onClick={() => { if (undo()) toast.info('Deshecho'); }}
              disabled={!canUndo}
              title="Deshacer (Ctrl+Z)"
              className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
              data-testid="undo-button"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-gray-200" />
            <button
              onClick={() => { if (redo()) toast.info('Rehecho'); }}
              disabled={!canRedo}
              title="Rehacer (Ctrl+Y)"
              className="w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
              data-testid="redo-button"
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mr-2">
            {saving ? (
              <>
                <Cloud className="w-3.5 h-3.5 animate-pulse" />
                Guardando...
              </>
            ) : dirty ? (
              <>
                <Cloud className="w-3.5 h-3.5 text-orange-500" />
                Cambios sin guardar
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5 text-green-500" />
                Guardado
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCaptureCover}
            disabled={capturingCover || activeTab !== 'model'}
            title={activeTab !== 'model' ? 'Ve a la pestaña de modelo para capturar' : 'Capturar imagen de portada del modelo 3D'}
            className="h-8 border-gray-200"
            data-testid="capture-cover-button"
          >
            {capturingCover ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
            Portada
          </Button>
          <div
            className="text-[10px] font-mono px-2 py-1 rounded bg-gray-100 text-gray-600"
            title="Próxima versión del .mcaddon (se incrementa en cada exportación)"
            data-testid="export-version"
          >
            v{project.export_version || '1.0.0'}
          </div>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="export-button"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            {t('editor.export')}
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 flex-shrink-0">
        <div className="flex gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {t(`editor.tabs.${tab.label}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {activeTab === 'model' && (
          <>
            {/* Toolbar */}
            <div className="w-14 bg-gray-900 flex flex-col items-center py-3 gap-1 flex-shrink-0">
              {TOOLS.map((tool) => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.id}
                    onClick={() => setActiveTool(tool.id)}
                    title={t(`editor.tools.${tool.label}`)}
                    className={`w-10 h-10 rounded flex items-center justify-center transition-colors ${
                      activeTool === tool.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                    data-testid={`tool-${tool.id}`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}

              <div className="w-8 h-px bg-gray-700 my-2" />

              {ACTIONS.map((action) => {
                const Icon = action.icon;
                const disabled = (action.id === 'duplicate' || action.id === 'delete') && selectedIds.length === 0;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleToolAction(action.id)}
                    disabled={disabled}
                    title={t(`editor.tools.${action.label}`)}
                    className={`w-10 h-10 rounded flex items-center justify-center transition-colors ${
                      disabled
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }`}
                    data-testid={`tool-${action.id}`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}

              <div className="w-8 h-px bg-gray-700 my-2" />

              <button
                onClick={() => cameraRef.current?.centerView?.()}
                title="Centrar vista"
                className="w-10 h-10 rounded flex items-center justify-center text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                data-testid="camera-center"
              >
                <Focus className="w-4 h-4" />
              </button>
            </div>

            {/* 3D Viewport */}
            <div className="flex-1 relative">
              <ThreeViewer
                model={model}
                selectedIds={selectedIds}
                onSelectCube={handleSelectCube}
                cameraControlsRef={cameraRef}
                transformMode={activeTool}
                onCubesTransform={handleCubesTransform}
              />

              <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur text-xs text-gray-300 px-2.5 py-1.5 rounded font-mono">
                {model.cubes.length} cubos
                {selectedIds.length > 0 && ` · ${selectedIds.length} seleccionado${selectedIds.length > 1 ? 's' : ''}`}
              </div>

              {activeTool !== 'select' && (
                <div className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-2.5 py-1.5 rounded">
                  Modo: {activeTool === 'translate' ? 'Mover' : activeTool === 'rotate' ? 'Rotar' : 'Escalar'}
                </div>
              )}

              <div className="absolute bottom-3 left-3 bg-gray-900/70 backdrop-blur text-[10px] text-gray-400 px-2 py-1 rounded font-mono">
                Ctrl+Clic: selección múltiple
              </div>
            </div>

            {/* Sidebar derecha: propiedades + outliner */}
            <div className="w-72 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
              {/* Properties */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  {t('editor.cube_properties')}
                  {selectedIds.length > 1 && (
                    <span className="ml-2 text-xs text-blue-600 font-normal">
                      ({selectedIds.length} seleccionados)
                    </span>
                  )}
                </h3>

                {!selectedCube ? (
                  <div className="text-center py-8">
                    <Box className="w-8 h-8 text-gray-300 mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-xs text-gray-500">{t('editor.no_selection')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium text-gray-700">Nombre</Label>
                      <Input
                        value={selectedCube.name}
                        onChange={(e) =>
                          updateModel(updateCube(model, primaryId, { name: e.target.value }))
                        }
                        className="mt-1 h-8 text-sm border-gray-200"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700 mb-1 block">
                        {t('editor.position')} (X, Y, Z)
                      </Label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[0, 1, 2].map((i) => (
                          <Input
                            key={i}
                            type="number"
                            step="1"
                            value={selectedCube.position[i]}
                            onChange={(e) => handleUpdateProperty('position', i, e.target.value)}
                            className="h-8 text-sm border-gray-200 px-2"
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700 mb-1 block">
                        {t('editor.size')} (W, H, D)
                      </Label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[0, 1, 2].map((i) => (
                          <Input
                            key={i}
                            type="number"
                            step="1"
                            min="1"
                            value={selectedCube.size[i]}
                            onChange={(e) => handleUpdateProperty('size', i, e.target.value)}
                            className="h-8 text-sm border-gray-200 px-2"
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700 mb-1 block">
                        {t('editor.rotation')} (X°, Y°, Z°)
                      </Label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[0, 1, 2].map((i) => (
                          <Input
                            key={i}
                            type="number"
                            step="15"
                            value={selectedCube.rotationDegrees[i]}
                            onChange={(e) => handleUpdateRotation(i, e.target.value)}
                            className="h-8 text-sm border-gray-200 px-2"
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-700 mb-1 block">
                        {t('editor.uv_offset')} (U, V)
                      </Label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[0, 1].map((i) => (
                          <Input
                            key={i}
                            type="number"
                            step="1"
                            min="0"
                            value={selectedCube.uv_offset[i]}
                            onChange={(e) => handleUpdateUV(i, e.target.value)}
                            className="h-8 text-sm border-gray-200 px-2"
                          />
                        ))}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 space-y-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDuplicateCubes}
                        className="w-full h-8 border-gray-200 justify-start text-sm"
                      >
                        <Copy className="w-3.5 h-3.5 mr-2" />
                        Duplicar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDeleteCubes}
                        className="w-full h-8 border-red-200 text-red-600 justify-start text-sm hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Outliner (estructura del modelo) */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Estructura del modelo
                  </h3>
                  <button
                    onClick={handleAddBone}
                    className="w-6 h-6 rounded bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                    title="Añadir bone"
                    data-testid="add-bone-btn"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-0.5 max-h-96 overflow-y-auto -mx-1 px-1">
                  {boneTree.map((bone) => {
                    const collapsed = collapsedBones[bone.name];
                    const isEmpty = bone.cubeObjs.length === 0;
                    // Ocultar bones vacíos genéricos que no tienen cubos
                    if (isEmpty && (bone.name === 'bone' || !bone.name)) return null;
                    return (
                      <div key={bone.id || bone.name}>
                        <button
                          onClick={() => toggleBoneCollapsed(bone.name)}
                          className="w-full flex items-center gap-1 px-1.5 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                          data-testid={`bone-${bone.name}`}
                        >
                          {collapsed ? (
                            <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          )}
                          {collapsed ? (
                            <Folder className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          ) : (
                            <FolderOpen className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          )}
                          <span className="font-medium truncate flex-1 text-left">{bone.displayName || bone.name}</span>
                          <span className="text-[10px] text-gray-400 font-mono">
                            {bone.cubeObjs.length}
                          </span>
                        </button>
                        {!collapsed && !isEmpty && (
                          <div className="ml-4 space-y-0.5 border-l border-gray-100">
                            {bone.cubeObjs.map((cube) => {
                              const isSel = selectedIds.includes(cube.id);
                              const isHidden = cube.visible === false;
                              return (
                                <div
                                  key={cube.id}
                                  className={`group flex items-center gap-1 pl-3 pr-1 py-1 rounded text-xs transition-colors ${
                                    isSel
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'hover:bg-gray-50 text-gray-700'
                                  }`}
                                >
                                  <button
                                    onClick={(e) => handleSelectCube(cube.id, {
                                      ctrlKey: e.ctrlKey || e.metaKey,
                                      shiftKey: e.shiftKey,
                                    })}
                                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                                    data-testid={`cube-list-item-${cube.id}`}
                                  >
                                    <Box className="w-3 h-3 flex-shrink-0" />
                                    <span className={`truncate ${isHidden ? 'italic text-gray-400' : ''}`}>
                                      {cube.name}
                                    </span>
                                  </button>
                                  <button
                                    onClick={() => handleToggleVisibility(cube.id)}
                                    className={`p-0.5 rounded transition-opacity ${
                                      isHidden ? 'opacity-100' : 'opacity-30 group-hover:opacity-100'
                                    } hover:bg-gray-200`}
                                    title={isHidden ? 'Mostrar' : 'Ocultar'}
                                    data-testid={`toggle-visibility-${cube.id}`}
                                  >
                                    {isHidden ? (
                                      <EyeOff className="w-3 h-3 text-gray-500" />
                                    ) : (
                                      <Eye className="w-3 h-3 text-gray-500" />
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
                  Los bones organizan el modelo. El icono <Eye className="w-2.5 h-2.5 inline" /> solo afecta al editor, no al addon exportado.
                </p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'texture' && (
          <ModelPainter model={model} onUpdate={updateModel} />
        )}

        {activeTab === 'behavior' && (
          <BehaviorsEditor
            behaviors={behaviors}
            onUpdate={updateBehaviors}
            behaviorConfig={behaviorConfig}
          />
        )}

        {activeTab === 'info' && (
          <div className="flex-1 bg-white overflow-y-auto">
            <div className="max-w-2xl mx-auto p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Información del proyecto</h2>
                <dl className="space-y-3">
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Nombre</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{project.name}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Autor</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{project.author}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Descripción</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{project.description || '—'}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Categoría</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{project.category}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Cubos</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{model.cubes.length}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100 items-center">
                    <dt className="text-sm text-gray-500">Versión del addon</dt>
                    <dd className="col-span-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={project.export_version || '1.0.0'}
                          onChange={(e) => updateProjectInfo({ export_version: e.target.value })}
                          placeholder="1.0.0"
                          className="w-28 h-8 text-sm font-mono text-center border-2 border-gray-200 rounded-md focus:border-blue-500 focus:outline-none bg-white"
                          data-testid="info-version-input"
                        />
                        <span className="text-xs text-gray-400">Este número aparecerá en Minecraft (formato X.Y.Z)</span>
                      </div>
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Eventos</dt>
                    <dd className="col-span-2 text-sm text-gray-900">{behaviors.events?.length || 0}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Resource Pack UUID</dt>
                    <dd className="col-span-2 text-xs font-mono text-gray-700">
                      {project.resource_pack_uuid}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                    <dt className="text-sm text-gray-500">Behavior Pack UUID</dt>
                    <dd className="col-span-2 text-xs font-mono text-gray-700">
                      {project.behavior_pack_uuid}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorPage;
